<template>
  <CSidebar position="fixed" :unfoldable="sidebarUnfoldable" :visible="sidebarVisible" @visible-change="
    (event) => {
      $store.commit({
        type: 'updateSidebarVisible',
        value: event
      })
    }
  ">
    <CSidebarBrand>
      <img :src="logo" :height="45" :width="175" />
    </CSidebarBrand>
    <AppSidebarNav />
  </CSidebar>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import logo from '@/assets/images/MOCO.png'
import { defineComponent, h, onMounted, ref, resolveComponent } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle
} from '@coreui/vue'
import { Button } from '@coreui/coreui'

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)

  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}
const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle
  },
  setup() {
    const route = useRoute()
    const firstRender = ref(true)

    if (sessionStorage.getItem('permissionsData') == null) {
      useRouter().push({ name: 'Login' })
    }

    onMounted(() => {
      firstRender.value = false
    })

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child))
            })
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon
              }),
              item.name
            ],
            default: () => item.items.map((child) => renderItem(child))
          }
        )
      }

      return item.to
        ? h(
          RouterLink,
          {
            to: item.to,
            custom: true
          },
          {
            default: (props) =>
              h(
                resolveComponent(item.component),
                {
                  active:
                    item.name == 'Tutorials' || item.name == 'FAQs'
                      ? false
                      : props.isActive,

                  href:
                    item.name == 'Tutorials'
                      ? 'javascript:void(0)'
                      : item.name == 'FAQs'
                        ? 'javascript:void(0)'
                        : props.href,

                  onClick: () => {
                    if (item.name == 'Tutorials') {
                      fetch('config.json')
                        .then((res) => res.json())
                        .then((config) => {
                          window.open(config.tutorialURL, '_blank')
                        })
                    } else if (item.name == 'FAQs') {
                      fetch('config.json')
                        .then((res) => res.json())
                        .then((config) => {
                          window.open(config.faqURL, '_blank')
                        })
                    } else {
                      props.navigate()
                    }
                  }
                },
                {
                  default: () => [
                    item.icon &&
                    h(resolveComponent('CIcon'), {
                      customClassName: 'nav-icon',
                      name: item.icon
                    }),
                    item.name,
                    item.badge &&
                    h(
                      CBadge,
                      {
                        class: 'ms-auto',
                        color: item.badge.color
                      },
                      {
                        default: () => item.badge.text
                      }
                    )
                  ]
                }
              )
          }
        )
        : h(
          resolveComponent(item.component),
          {},
          {
            default: () => item.name
          }
        )
    }
    var navList = [
      {
        component: 'CNavItem',
        name: 'Dashboard',
        to: '/dashboard',
        icon: '',
        badge: {
          color: 'primary'
        }
      }
    ]

    if (
      JSON.parse(
        sessionStorage.getItem('permissionsData')
      ).terminalPermissions.includes('View')
    ) {
      navList.push({
        component: 'CNavItem',
        name: 'Terminal',
        to: '/terminal',
        icon: '',
        badge: {
          color: 'primary'
        }
      })
    }

    if (
      JSON.parse(
        sessionStorage.getItem('permissionsData')
      ).operatorPermissions.includes('View')
    ) {
      navList.push({
        component: 'CNavItem',
        name: 'Operator',
        to: '/operator',
        icon: '',
        badge: {
          color: 'primary'
        }
      })
    }

    if (
      JSON.parse(
        sessionStorage.getItem('permissionsData')
      ).transactionPermissions.includes('View')
    ) {
      navList.push({
        component: 'CNavItem',
        name: 'Transactions',
        to: '/transactions',
        icon: '',
        badge: {
          color: 'primary'
        }
      })
    }

    navList.push({
      component: 'CNavItem',
      name: 'Advice',
      to: '/advice',
      icon: '',
      badge: {
        color: 'primary'
      }
    })

    navList.push({
      component: 'CNavItem',
      name: 'Download QR',
      to: '/downloadQR',
      icon: '',
      badge: {
        color: 'primary'
      }
    })

    if (
      JSON.parse(
        sessionStorage.getItem('permissionsData')
      ).reportPermissions.includes('View Analytics Report')
    ) {
      navList.push({
        component: 'CNavItem',
        name: 'Merchant Analytics',
        to: '/analyticsReport',
        icon: '',
        badge: {
          color: 'primary'
        }
      })
    }

    if (
      JSON.parse(sessionStorage.getItem('permissionsData')).merchantPermissions
        .length > 0
    ) {
      navList.push({
        component: 'CNavItem',
        name: 'Merchant Information',
        to: '/viewMerchantInformation',
        icon: '',
        badge: {
          color: 'primary'
        }
      })
    }

    navList.push({
      component: 'CNavItem',
      name: 'Activity Log',
      to: '/activity-log',
      badge: {
        color: 'primary'
      }
    })

    navList.push({
      component: 'CNavItem',
      name: 'Tutorials',
      to: window.location.href.split('#')[1],
      icon: '',
      badge: {
        color: 'primary'
      }
    })

    navList.push({
      component: 'CNavItem',
      name: 'FAQs',
      to: window.location.href.split('#')[1],
      icon: '',
      badge: {
        color: 'primary'
      }
    })

    if (sessionStorage.getItem('mobileView') === 'true') {
      navList.push(
        {
          component: 'CNavItem',
          name: 'Change PIN',
          to: '/operator/changePIN',
          icon: '',
          badge: {
            color: 'primary'
          }
        },
        {
          component: 'CNavItem',
          name: 'Logout',
          to: '/login',
          icon: '',
          badge: {
            color: 'primary'
          }
        }
      )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => navList.map((item) => renderItem(item))
        }
      )
  }
})

export default {
  name: 'AppSidebar',
  components: {
    AppSidebarNav,
    Button
  },
  data() {
    return {
      isMobileView: sessionStorage.getItem('mobileView')
    }
  },
  setup() {
    const store = useStore()
    return {
      logo,
      sidebarUnfoldable: computed(() => store.state.sidebarUnfoldable),
      sidebarVisible: computed(() => store.state.sidebarVisible)
    }
  },
  methods: {}
}
</script>

<style scoped>
.sidebar-nav {
  background-color: #010047;
  /* border-style: none solid none none; */
}

.sidebar-brand {
  background-color: #010047;
}
</style>

<style>
.sidebar-nav .nav-link.active {
  color: #f37235;
  background: #010047 !important;
}

.sidebar-nav .nav-link {
  color: white;
  font-weight: bold;
  /* border-style: none none solid none;
  border-color: white; */
  border-width: 1px;
  height: 45px;
}

.sidebar-nav .nav-link:hover {
  color: #f37235;
  font-weight: bold;
  /* border-style: none none solid none;
  border-color: white; */
}
</style>
